import { bunja } from 'bunja';
import { createScopeFromContext } from 'bunja/react';
import { atom } from 'jotai';
import { createContext } from 'react';

import { OrderTypeEnum } from '@flipster/apis/types';
import { triggerPriceTypeEnum, type TriggerPriceType } from '@flipster/apis/v2/trade/types';
import { marginsBunja } from '@flipster/r230522/original/margin';
import { marketsBunja } from '@flipster/r230522/original/market';
import { pswapTickersBunja } from '@flipster/r230522/original/pswap-ticker';
import { pswapsBunja } from '@flipster/r230522/original/pswaps';
import { getTickerDisplayPriceOrDefault } from '@flipster/r230522/original/ticker-price';
import { atomWithBigNumber } from '@flipster/react/misc/state/atom-with-bignumber';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { BigNumber } from '@flipster/utils/num';
import { OrderMarginType } from '@flipster/utils/types/base';

import { estimateOrderLiqPrice } from '../../shared/liquidation';
import { positionBracketBunja } from '../../shared/position-bracket/state';
import { rawInstrumentsKeyBySymbolAtom } from '../../shared/snapshot/state';
import { SymbolScope } from '../../shared/symbol/bunja';
import { PositionTypeScope, positionTpslBunja } from '../../shared/tpsl/bunja';
import { ModalDismissSignal } from '../../utils/modal-error-boundary';

export function resolveTriggerReferencePrice(
  triggerPriceType: TriggerPriceType,
  prices: { lastPrice: BigNumber; markPrice: BigNumber },
): BigNumber {
  return triggerPriceType === triggerPriceTypeEnum.mark ? prices.markPrice : prices.lastPrice;
}

export const MarginTypeContext = createContext<OrderMarginType | undefined>(undefined);
export const MarginTypeScope = createScopeFromContext(MarginTypeContext);

export const parentPositionBunja = bunja(() => {
  const symbol = bunja.use(SymbolScope);
  const positionType = bunja.use(PositionTypeScope);
  const marginType = bunja.use(MarginTypeScope);
  const { instrumentAtom, pricePrecisionAtom, quoteCurrencyAtom } = bunja.use(instrumentBunja);
  const shared = bunja.use(positionTpslBunja);
  // WS??specs 브래킷 해석은 positionBracketBunja 한 곳에서만 (ERD-240 §10, 단일 해석 지점).
  const { positionBracketAtom } = bunja.use(positionBracketBunja);
  const { pswapTickersAtom } = bunja.use(pswapTickersBunja);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { pswapsAtom } = bunja.use(pswapsBunja);
  const { getMarginAtom } = bunja.use(marginsBunja);

  const markPriceAtom = atom(
    (get) =>
      get(pswapTickersAtom)?.[symbol]?.markPrice ??
      get(marketsAtom)?.[symbol]?.markPrice ??
      get(shared.orderPriceAtom).toString(),
  );

  // Position TP/SL: server-published `liquidation_price` is authoritative.
  // Render it as-is regardless of margin mode.
  const liqPriceForOpenPositionAtom = atom((get) => get(shared.openPositionAtom)?.liquidationPrice?.toString() || '0');

  // Order TP/SL: simulate the parent order to obtain the post-fill state and
  // call `estimateOrderLiqPrice`. Same one-way / multi-position rule as the
  // order form — but here, the modal opens against a single pending order, so
  // there's no live position to merge into either way.
  const liqPriceForPendingPositionAtom = atom((get) => {
    const instrument = get(instrumentAtom);
    const pswap = get(pswapsAtom)?.[symbol];
    const positionBracket = get(positionBracketAtom);
    if (!instrument || !positionBracket) return '0';

    const margin = get(getMarginAtom)[get(quoteCurrencyAtom)];
    const feeRate = BigNumber.max(pswap?.takerFeeRate ?? '0', pswap?.makerFeeRate ?? '0').toString();
    const pricePrecision = get(pricePrecisionAtom);
    const isLong = get(shared.sideAtom) === 'Long';

    const result = estimateOrderLiqPrice({
      targetPosition: null,
      order: {
        side: get(shared.sideAtom),
        qty: get(shared.orderQtyAtom).toString(),
        price: get(shared.orderPriceAtom).toString(),
        initLeverage: get(shared.leverageAtom),
        feeRate,
        marginMode: marginType ?? 'Isolated',
      },
      markPrice: get(markPriceAtom),
      targetSymbol: symbol,
      crossMarginBalance: margin?.marginBalance ?? '0',
      maintPositionMargin: margin?.maintPositionMargin,
      tickSize: instrument.tickSize,
      positionBracket,
    });

    if (result === null) return '0';
    return Num(result).toFixed(pricePrecision, isLong ? BigNumber.ROUND_UP : BigNumber.ROUND_DOWN);
  });
  const liqPriceAtom = positionType === 'open' ? liqPriceForOpenPositionAtom : liqPriceForPendingPositionAtom;
  const entryOrOrderPriceAtom = positionType === 'open' ? shared.entryPriceAtom : shared.orderPriceAtom;
  // Cross margin now produces a meaningful estimate (with disclaimer); the
  // only remaining gate is whether we have any liq price source at all.
  const canHaveLiqPrice = true;

  const isLimitOrderAtom = atom((get) =>
    Boolean(get(shared.pendingPositionAtom)?.orderType === OrderTypeEnum['Limit']),
  );
  const isTimeTriggerOrderAtom = atom((get) => Boolean(get(shared.pendingPositionAtom)?.triggerTimestamp));

  const openPositionQtyAtom = atom((get) => get(shared.openPositionAtom)?.position || '0');
  // Value-equal BigNumber so socket row identity churn does not thrash qty subscribers.
  const openPositionAbsQtyAtom = atomWithBigNumber((get) => Num(get(openPositionQtyAtom)).abs());
  const positionQtyAtom = positionType === 'open' ? openPositionAbsQtyAtom : shared.orderQtyAtom;

  const dismissalGuardAtom = atom((get) => {
    if (positionType !== 'open') return null;
    const position = get(shared.openPositionAtom);
    if (!position) throw new ModalDismissSignal('Position not found');
    return null;
  });

  return {
    ...shared,
    liqPriceAtom,
    entryOrOrderPriceAtom,
    canHaveLiqPrice,
    positionType,
    marginType,
    isLimitOrderAtom,
    isTimeTriggerOrderAtom,
    positionQtyAtom,
    dismissalGuardAtom,
  };
});
parentPositionBunja.debugLabel = 'parentPositionBunja';

export const instrumentBunja = bunja(() => {
  const symbol = bunja.use(SymbolScope);

  const instrumentAtom = atom((get) => get(rawInstrumentsKeyBySymbolAtom)?.[symbol]);
  const tickSizeAtom = atom((get) => Num(get(instrumentAtom)?.tickSize || 0));
  const pricePrecisionAtom = atom((get) => get(instrumentAtom)?.pricePrecision || 2);
  const notionalMinOrderAmountAtom = atom((get) => Num(get(instrumentAtom)?.notionalMinOrderAmount || 0));
  const maintenanceMarginRateAtom = atom((get) => Num(get(instrumentAtom)?.maintMarginRate || 0));
  const tradeQtyPrecisionAtom = atom((get) => get(instrumentAtom)?.tradeQtyPrecision || 2);
  const unitOrderQtyAtom = atom((get) => Num(get(instrumentAtom)?.unitOrderQty || 0));
  const positionUnitAtom = atom((get) => get(instrumentAtom)?.positionUnit || '');
  const displayShortNameAtom = atom((get) => get(instrumentAtom)?.displayShortName || '');
  const quoteCurrencyAtom = atom((get) => get(instrumentAtom)?.quoteCurrency || 'USDT');
  const quoteCurrencyPrecisionAtom = atom((get) => {
    const quoteCurrency = get(quoteCurrencyAtom);
    return getQuoteCurrencyPrecision(quoteCurrency);
  });

  return {
    instrumentAtom,
    tickSizeAtom,
    pricePrecisionAtom,
    notionalMinOrderAmountAtom,
    maintenanceMarginRateAtom,
    tradeQtyPrecisionAtom,
    unitOrderQtyAtom,
    positionUnitAtom,
    displayShortNameAtom,
    quoteCurrencyAtom,
    quoteCurrencyPrecisionAtom,
  };
});
instrumentBunja.debugLabel = 'instrumentBunja';

export const tickerPriceBunja = bunja(() => {
  const symbol = bunja.use(SymbolScope);
  const { marketsAtom } = bunja.use(marketsBunja);
  const { pswapTickersAtom } = bunja.use(pswapTickersBunja);

  const marketAtom = atom((get) => get(marketsAtom)?.[symbol]);
  const pswapTickerAtom = atom((get) => get(pswapTickersAtom)?.[symbol]);
  // atomWithBigNumber: ticker table identity changes every socket tick; only propagate when the
  // numeric price actually changes (avoids Position TPSL modal re-render storms).
  const lastPriceAtom = atomWithBigNumber((get) => {
    const pswapTicker = get(pswapTickerAtom);
    const market = get(marketAtom);
    return Num(getTickerDisplayPriceOrDefault(pswapTicker, market));
  });
  const markPriceAtom = atomWithBigNumber((get) => {
    const pswapTicker = get(pswapTickerAtom);
    const market = get(marketAtom);
    const markPrice = pswapTicker?.markPrice ?? market?.markPrice;
    return markPrice != null ? Num(markPrice) : get(lastPriceAtom);
  });

  return { lastPriceAtom, markPriceAtom };
});
tickerPriceBunja.debugLabel = 'tickerPriceBunja';
